import React, { useMemo } from "react";
import { Badge } from "../../../../../ui/components/Badge";
import type {
  DocumentChangeFieldFrequencyItemEntity,
  DocumentChangeReportSummaryEntity,
} from "../../../domain/entities/DocumentChangeAuditEntity";
import {
  getHeaderAllowlist,
  getLineAllowlist,
} from "../../../../../shared/utils/review";
import type { InvoiceOrBillKind } from "../../../../../shared/utils/review";

interface MostEditedFieldsPanelProps {
  summary: DocumentChangeReportSummaryEntity | null;
  entityTypeFilter: "" | "bill" | "invoice";
}

const humanizeKey = (key: string): string =>
  key
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const buildLabelResolver = (
  kind: InvoiceOrBillKind,
): ((item: DocumentChangeFieldFrequencyItemEntity) => string) => {
  const headerByKey = new Map<string, string>();
  getHeaderAllowlist(kind).forEach((f) => headerByKey.set(f.key, f.label));
  const lineByKey = new Map<string, string>();
  getLineAllowlist(kind).forEach((f) => lineByKey.set(f.key, f.label));

  return (item) => {
    if (item.scope === "line") {
      return lineByKey.get(item.field) ?? humanizeKey(item.field);
    }
    if (item.field === "gst_no") {
      return kind === "bill" ? "Vendor GST No" : "Customer GST No";
    }
    return headerByKey.get(item.field) ?? humanizeKey(item.field);
  };
};

const FieldFrequencyColumn: React.FC<{
  title: string;
  badgeVariant: "warning" | "brand";
  items: DocumentChangeFieldFrequencyItemEntity[];
  kind: InvoiceOrBillKind;
  chipClass: string;
  countClass: string;
}> = ({ title, badgeVariant, items, kind, chipClass, countClass }) => {
  const resolveLabel = useMemo(() => buildLabelResolver(kind), [kind]);
  const totalCorrections = items.reduce((sum, item) => sum + item.count, 0);

  return (
    <div className="flex min-w-[280px] flex-1 flex-col items-start gap-4 rounded-lg border border-solid border-neutral-border bg-default-background p-5">
      <div className="flex w-full items-center justify-between">
        <Badge variant={badgeVariant}>{title}</Badge>
        <span className="text-caption font-caption text-subtext-color">
          {totalCorrections} correction{totalCorrections === 1 ? "" : "s"}
        </span>
      </div>

      {items.length === 0 ? (
        <div className="flex w-full items-center justify-center rounded-md border border-dashed border-neutral-border py-5">
          <span className="text-caption font-caption text-subtext-color">
            No field edits recorded yet
          </span>
        </div>
      ) : (
        <div className="flex w-full flex-wrap gap-2">
          {items.map((item) => (
            <span
              key={`${item.scope}:${item.field}`}
              className={`inline-flex items-center gap-2 rounded-full border border-solid py-1 pl-3 pr-1.5 ${chipClass}`}
            >
              <span className="text-caption font-caption text-default-font">
                {item.scope === "line" ? (
                  <span className="text-subtext-color">Line · </span>
                ) : null}
                {resolveLabel(item)}
              </span>
              <span
                className={`inline-flex min-w-[20px] items-center justify-center rounded-full px-1.5 text-caption font-caption font-semibold ${countClass}`}
              >
                {item.count}
              </span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export const MostEditedFieldsPanel: React.FC<MostEditedFieldsPanelProps> = ({
  summary,
  entityTypeFilter,
}) => {
  if (!summary) {
    return (
      <div className="flex w-full flex-col gap-4">
        <div className="h-5 w-40 rounded bg-neutral-200" />
        <div className="flex w-full flex-wrap gap-4">
          {Array.from({ length: 2 }).map((_, index) => (
            <div
              key={index}
              className="flex min-w-[280px] flex-1 flex-col gap-4 rounded-lg border border-solid border-neutral-border bg-neutral-50 p-5"
            >
              <div className="h-5 w-24 rounded bg-neutral-200" />
              <div className="flex flex-wrap gap-2">
                {Array.from({ length: 5 }).map((__, chip) => (
                  <div key={chip} className="h-7 w-24 rounded-full bg-neutral-200" />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const showBills = entityTypeFilter === "" || entityTypeFilter === "bill";
  const showInvoices = entityTypeFilter === "" || entityTypeFilter === "invoice";

  const billItems = summary.fieldFrequency.bill;
  const invoiceItems = summary.fieldFrequency.invoice;

  if (billItems.length === 0 && invoiceItems.length === 0) {
    return null;
  }

  return (
    <div className="flex w-full flex-col items-start gap-4">
      <div className="flex flex-col items-start gap-1">
        <span className="text-heading-3 font-heading-3 text-default-font">
          Most Edited Fields
        </span>
        <span className="text-caption font-caption text-subtext-color">
          Fields most often corrected after extraction — the number is how many
          times each was fixed. Open a document to see the exact change.
        </span>
      </div>

      <div className="flex w-full flex-wrap items-stretch gap-4">
        {showBills ? (
          <FieldFrequencyColumn
            title="Bills"
            badgeVariant="warning"
            items={billItems}
            kind="bill"
            chipClass="border-amber-200 bg-amber-50"
            countClass="bg-amber-100 text-amber-700"
          />
        ) : null}
        {showInvoices ? (
          <FieldFrequencyColumn
            title="Invoices"
            badgeVariant="brand"
            items={invoiceItems}
            kind="invoice"
            chipClass="border-blue-200 bg-blue-50"
            countClass="bg-blue-100 text-blue-700"
          />
        ) : null}
      </div>
    </div>
  );
};
